import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Checkbox,
  FormGroup,
  FormControlLabel,
  DialogContentText,
  Card,
  CardContent,
  CardActionArea,
  Grid,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import api from '../utils/api';
import PassScanner from './PassScanner';
import { useNavigate, useParams } from 'react-router-dom';

const AccessPoints = () => {
  const [accessPoints, setAccessPoints] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [name, setName] = useState('');
  const [availablePassTypes, setAvailablePassTypes] = useState([]);
  const [selectedPassTypes, setSelectedPassTypes] = useState([]);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [selectedAccessPoint, setSelectedAccessPoint] = useState(null);
  const [isCounter, setIsCounter] = useState(false);
  const [isEntryExit, setIsEntryExit] = useState(false);
  const [privilege, setPrivilege] = useState('');
  const [availablePrivileges, setAvailablePrivileges] = useState([]);
  
  // New state for event selection
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventSelectionOpen, setEventSelectionOpen] = useState(true);
  
  // Add a new state variable for event ID
  const [selectedEventId, setSelectedEventId] = useState(null);
  
  const navigate = useNavigate();
  const { eventName } = useParams();

  useEffect(() => {
    // If we have an event name in the URL, use it
    if (eventName) {
      setSelectedEvent(eventName);
      setEventSelectionOpen(false);
      
      // First fetch the event ID for the given name
      const getEventData = async () => {
        try {
          const response = await api.get(`/api/list-events/?event=${eventName}`);
          if (response.data && response.data.length > 0) {
            setSelectedEventId(response.data[0].id);
            // Only after we have the event ID, fetch the other data
            fetchAccessPoints();
            fetchPassTypes();
            fetchPrivileges();
          }
        } catch (error) {
          console.error('Error fetching event ID:', error);
        }
      };
      
      getEventData();
    } else {
      // Otherwise fetch available events
      fetchEvents();
    }
  }, [eventName]);

  // Add a separate useEffect to ensure privileges are fetched when selectedEvent changes
  useEffect(() => {
    if (selectedEvent && !eventSelectionOpen) {
      console.log('Fetching privileges for event:', selectedEvent);
      fetchPrivileges();
    }
  }, [selectedEvent, eventSelectionOpen]);

  // Add a separate useEffect for access points
  useEffect(() => {
    if (selectedEvent && !eventSelectionOpen) {
      console.log('Fetching access points for event:', selectedEvent);
      fetchAccessPoints();
    }
  }, [selectedEvent, eventSelectionOpen]);

  const fetchEvents = async () => {
    try {
      const response = await api.get('/api/list-events/');
      setEvents(response.data);
      console.log('Fetched events:', response.data);
    } catch (error) {
      console.error('Error fetching events:', error);
    }
  };

  const handleEventSelect = (event) => {
    setSelectedEvent(event.name);
    setSelectedEventId(event.id); // Store the event ID
    setEventSelectionOpen(false);
    // Navigate to the event-specific URL
    navigate(`/admin-dashboard/access-points/${event.name}`);
  };

  const fetchAccessPoints = async () => {
    if (!selectedEvent) return;
    
    console.log('Making API request for access points with event:', selectedEvent);
    try {
      // Include the selected event in the API request
      const response = await api.get(`/api/access-points/?event=${selectedEvent}`);
      console.log('Access points response:', response.data);
      setAccessPoints(response.data);
    } catch (error) {
      console.error('Error fetching access points:', error);
    }
  };

  const fetchPassTypes = async () => {
    if (!selectedEvent) return;
    
    try {
      // Include the selected event in the API request
      const response = await api.get(`/api/unique-access-points/?event=${selectedEvent}`);
      setAvailablePassTypes(response.data);
    } catch (error) {
      console.error('Error fetching pass types:', error);
    }
  };

  const fetchPrivileges = async () => {
    if (!selectedEvent) return;
    
    try {
      // Include the selected event in the API request
      const response = await api.get(`/api/unique-privilege/?event=${selectedEvent}`);
      setAvailablePrivileges(response.data);
      console.log('Fetched privileges:', response.data);
    } catch (error) {
      console.error('Error fetching privileges:', error);
      // Set empty array as fallback when API fails
      setAvailablePrivileges([]);
    }
  };

  const handleOpen = () => {
    setOpen(true);
    setEditingId(null);
    setName('');
    setSelectedPassTypes([]);
    setIsCounter(false);
    setIsEntryExit(false);
    setPrivilege('');
  };

  const handleClose = () => {
    setOpen(false);
    setEditingId(null);
  };

  const handleEdit = (accessPoint) => {
    setEditingId(accessPoint.id);
    setName(accessPoint.name);
    setSelectedPassTypes(accessPoint.pass_types);
    setIsCounter(accessPoint.is_counter);
    setIsEntryExit(accessPoint.is_entry_exit);
    setPrivilege(accessPoint.privilage || '');
    setOpen(true);
  };

  const handleSubmit = async () => {
    const data = { 
      name, 
      pass_types: selectedPassTypes,
      is_counter: isCounter,
      is_entry_exit: isEntryExit,
      privilage: privilege,
      event: selectedEventId // Use the event ID instead of name
    };

    try {
      if (editingId) {
        await api.put(`/api/access-points/${editingId}/`, data);
      } else {
        await api.post('/api/access-points/', data);
      }
      fetchAccessPoints();
      handleClose();
    } catch (error) {
      console.error('Error saving access point:', error);
    }
  };

  const handlePassTypeChange = (passType) => {
    setSelectedPassTypes(prev =>
      prev.includes(passType)
        ? prev.filter(type => type !== passType)
        : [...prev, passType]
    );
  };

  const handleDeleteConfirmOpen = (id) => {
    setDeleteId(id);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirmClose = () => {
    setDeleteConfirmOpen(false);
    setDeleteId(null);
  };

  const handleDeleteConfirm = async () => {
    try {
      await api.delete(`/api/access-points/${deleteId}/`);
      fetchAccessPoints();
      handleDeleteConfirmClose();
    } catch (error) {
      console.error('Error deleting access point:', error);
    }
  };

  const handleAccessPointClick = (accessPoint) => {
    setSelectedAccessPoint(accessPoint);
  };

  const handleBackToList = () => {
    setSelectedAccessPoint(null);
  };

  // Event selection screen
  if (eventSelectionOpen) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>
          Select an Event
        </Typography>
        {events.length === 0 ? (
          <Typography>Loading events...</Typography>
        ) : (
          <Grid container spacing={3}>
            {events.map((event) => (
              <Grid item xs={12} sm={6} md={4} key={event.id}>
                <Card>
                  <CardActionArea onClick={() => handleEventSelect(event)}>
                    <CardContent>
                      <Typography variant="h5" component="div">
                        {event.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {event.description || 'No description available'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {new Date(event.start_date).toLocaleDateString()} - {new Date(event.end_date).toLocaleDateString()}
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    );
  }

  if (selectedAccessPoint) {
    return <PassScanner 
      accessPointId={selectedAccessPoint.id} 
      onBack={handleBackToList} 
      eventName={selectedEvent}
      accessPointName={selectedAccessPoint.name}
      isEntryExit={selectedAccessPoint.is_entry_exit}
    />;
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Access Points - {selectedEvent}
      </Typography>
      <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={handleOpen}>
        Add Access Point
      </Button>
      <List>
        {accessPoints.map((accessPoint) => (
          <ListItem
            key={accessPoint.id}
            button
            onClick={() => handleAccessPointClick(accessPoint)}
            secondaryAction={
              <Box>
                <IconButton edge="end" aria-label="edit" onClick={(e) => {
                  e.stopPropagation();
                  handleEdit(accessPoint);
                }}>
                  <EditIcon />
                </IconButton>
                <IconButton edge="end" aria-label="delete" onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteConfirmOpen(accessPoint.id);
                }}>
                  <DeleteIcon />
                </IconButton>
              </Box>
            }
          >
            <ListItemText
              primary={accessPoint.name}
              secondary={
                <Box>
                  {accessPoint.pass_types.map((type, index) => (
                    <Chip key={index} label={type} size="small" style={{ marginRight: 4 }} />
                  ))}
                  {accessPoint.is_entry_exit && (
                    <Chip 
                      label="Entry/Exit" 
                      size="small" 
                      color="primary" 
                      style={{ marginRight: 4 }} 
                    />
                  )}
                  {accessPoint.is_counter && (
                    <Chip 
                      label="Counter" 
                      size="small" 
                      color="secondary" 
                      style={{ marginRight: 4 }} 
                    />
                  )}
                </Box>
              }
            />
          </ListItem>
        ))}
      </List>
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>{editingId ? 'Edit Access Point' : 'Add Access Point'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Name"
            fullWidth
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={isCounter}
                onChange={(e) => setIsCounter(e.target.checked)}
              />
            }
            label="Is Counter"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={isEntryExit}
                onChange={(e) => setIsEntryExit(e.target.checked)}
              />
            }
            label="Is Entry/Exit"
          />
          <TextField
            select
            margin="dense"
            fullWidth
            value={privilege}
            onChange={(e) => setPrivilege(e.target.value)}
            SelectProps={{
              native: true,
            }}
          >
            <option value="">Select a privilege</option>
            {availablePrivileges.map((priv) => (
              <option key={priv} value={priv}>
                {priv}
              </option>
            ))}
          </TextField>
          <Typography variant="subtitle1" style={{ marginTop: 16 }}>Pass Types:</Typography>
          <FormGroup>
            {availablePassTypes.map((passType) => (
              <FormControlLabel
                key={passType}
                control={
                  <Checkbox
                    checked={selectedPassTypes.includes(passType)}
                    onChange={() => handlePassTypeChange(passType)}
                  />
                }
                label={passType}
              />
            ))}
          </FormGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSubmit}>{editingId ? 'Update' : 'Add'}</Button>
        </DialogActions>
      </Dialog>
      
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleDeleteConfirmClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{"Confirm Deletion"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete this access point? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteConfirmClose}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AccessPoints;